import { ScanBarcode } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "../../icons/Icon";
import { cx } from "../../styles";

/**
 * The field a warehouse screen lives in.
 *
 * A hardware barcode scanner is a keyboard. It types the code and presses
 * Enter, faster than a person can — and that is the whole of the heuristic
 * below. What makes this a component rather than an `<input>` is everything
 * around the typing.
 *
 * **It takes focus back.** A picker scans, the screen updates, and the next
 * scan has to land somewhere. If focus moved to whatever the update rendered,
 * the next barcode is typed into a table or nowhere at all, and the person
 * finds out when the count is wrong. So focus returns after every scan and
 * whenever the window regains it — unless somebody has deliberately put it in
 * another input, which is the one case where taking it back would be fighting
 * them.
 *
 * **`paused` exists for overlays.** A drawer over a scan screen traps focus, and
 * a field underneath that keeps grabbing it back would make the drawer
 * unusable. The screen sets `paused` while one is open.
 *
 * **Enter is not the only trigger.** Some scanners are configured without a
 * suffix. A burst of characters arriving faster than hands can type, followed
 * by a pause, is a scan; the thresholds below are the definition.
 */

/** Gaps shorter than this between keys mean a machine, not a person. */
export const SCAN_INTERVAL_MS = 50;

/** How long a burst must go quiet before it counts as finished. */
export const SCAN_IDLE_MS = 60;

/** Shorter than this is somebody typing, not a barcode. */
export const SCAN_MIN_LENGTH = 4;

export interface BarcodeScanFieldProps {
	/** Called once per scan, with the code and an empty field afterwards. */
	onScan: (code: string) => void;
	/** Stop taking focus. Set while a dialog or drawer is open. */
	paused?: boolean;
	/** Take focus on mount. True for a screen whose only job is scanning. */
	autoFocus?: boolean;
	placeholder?: string;
	disabled?: boolean;
	id?: string;
	name?: string;
	"aria-label"?: string | undefined;
	"aria-describedby"?: string | undefined;
	className?: string;
}

export function BarcodeScanField({
	onScan,
	paused = false,
	autoFocus = true,
	placeholder,
	disabled = false,
	id,
	name,
	className,
	"aria-label": ariaLabel,
	"aria-describedby": describedBy,
}: BarcodeScanFieldProps) {
	const input = useRef<HTMLInputElement>(null);
	const [text, setText] = useState("");

	// The burst being watched. Refs rather than state: a keystroke must not
	// re-render, and the timer callback needs the current values rather than
	// the ones from the render it was created in.
	const lastKeyAt = useRef(0);
	const fastKeys = useRef(0);
	const idleTimer = useRef<number | undefined>(undefined);

	const fire = useCallback(
		(code: string) => {
			window.clearTimeout(idleTimer.current);
			fastKeys.current = 0;

			const trimmed = code.trim();
			setText("");
			if (trimmed === "") {
				return;
			}

			onScan(trimmed);
			// Back to the field, because the next carton is already in somebody's
			// hand. Whatever the scan rendered does not get to keep focus.
			if (!paused) {
				input.current?.focus();
			}
		},
		[onScan, paused],
	);

	function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
		if (event.key === "Enter") {
			// Never submits the surrounding form. A scan screen inside a form
			// would otherwise save a half-filled record on every carton.
			event.preventDefault();
			fire(event.currentTarget.value);
			return;
		}

		// One printable character. Modifiers and arrows say nothing about speed.
		if (event.key.length !== 1) {
			return;
		}

		const now = Date.now();
		fastKeys.current = now - lastKeyAt.current < SCAN_INTERVAL_MS ? fastKeys.current + 1 : 0;
		lastKeyAt.current = now;
	}

	function onChange(event: React.ChangeEvent<HTMLInputElement>) {
		const next = event.target.value;
		setText(next);

		window.clearTimeout(idleTimer.current);
		if (fastKeys.current + 1 < SCAN_MIN_LENGTH || next.trim() === "") {
			return;
		}

		// A burst that has gone quiet. The timer is restarted on every keystroke,
		// so it only fires once the scanner has actually stopped.
		idleTimer.current = window.setTimeout(() => fire(next), SCAN_IDLE_MS);
	}

	// Take focus on mount, and take it back when the window returns — somebody
	// who alt-tabbed to a manifest and came back should be able to scan without
	// clicking first.
	useEffect(() => {
		if (paused || disabled) {
			return;
		}
		if (autoFocus) {
			input.current?.focus();
		}

		function onWindowFocus() {
			// Not if somebody has deliberately put the cursor somewhere else.
			// Stealing focus out of a search box is worse than missing one scan.
			const active = document.activeElement;
			const elsewhere =
				active !== null &&
				active !== document.body &&
				active !== input.current &&
				active.matches("input, textarea, select, [contenteditable]");

			if (!elsewhere) {
				input.current?.focus();
			}
		}

		window.addEventListener("focus", onWindowFocus);
		return () => window.removeEventListener("focus", onWindowFocus);
	}, [paused, disabled, autoFocus]);

	// Nothing pending should outlive the component.
	useEffect(() => () => window.clearTimeout(idleTimer.current), []);

	return (
		<div
			className={cx(
				// Two pixels of --ring and a 3px brand glow, from the design: the
				// field is meant to look armed, because on a scan screen it is the
				// one thing that matters and the person is not looking at the cursor.
				"flex h-9 items-center gap-2 rounded-sm border-2 border-ring bg-bg-surface px-2.5",
				"ring-3 ring-ring-halo",
				paused && "border-border-strong ring-0",
				"max-sm:min-h-12",
				className,
			)}
		>
			<Icon as={ScanBarcode} size={18} className="shrink-0 text-brand-ink" />
			<input
				ref={input}
				id={id}
				name={name}
				value={text}
				disabled={disabled}
				placeholder={placeholder}
				aria-label={ariaLabel}
				aria-describedby={describedBy}
				onKeyDown={onKeyDown}
				onChange={onChange}
				// A scanner sends the code as-is. Every one of these would change it:
				// autocomplete offers a previous scan, autocapitalize alters a
				// lowercase code, and a spell-checker underlines all of them.
				autoComplete="off"
				autoCorrect="off"
				autoCapitalize="off"
				spellCheck={false}
				className={cx(
					"min-w-0 flex-1 bg-transparent font-mono text-[13px] text-fg outline-none",
					"placeholder:font-sans placeholder:text-fg-subtle",
					"disabled:cursor-not-allowed disabled:text-fg-subtle",
				)}
			/>
		</div>
	);
}
